import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Building2, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../lib/api.js";
import { useAuth } from "../auth.jsx";
import { useOnboarding } from "../lib/onboarding.jsx";
import { Card, Spinner } from "./ui.jsx";

// Company details + email / phone verification, in one place. These are the invoice details
// (the old "Billing information" card) AND what the deploy gate checks, so there is a single
// form rather than two that could disagree.

const FIELDS = ["billing_company", "billing_address", "billing_country", "billing_email", "billing_phone", "billing_vat"];
const DETAIL_KEY = { billing_company: "company", billing_address: "address", billing_country: "country", billing_email: "email", billing_phone: "phone", billing_vat: "vat" };
// The server stores the country as an ISO 3166-1 alpha-2 code ("GB"), so this is a select of
// codes, not free text. Names come from the browser (Intl.DisplayNames) — no data to ship.
const ISO_COUNTRIES = (
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ " +
  "CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR " +
  "GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP " +
  "KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT " +
  "MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW " +
  "SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ " +
  "UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW"
).split(" ");
const COUNTRY_OPTIONS = (() => {
  let names = null;
  try { names = new Intl.DisplayNames(undefined, { type: "region" }); } catch { /* very old browser: show codes */ }
  const name = (c) => { try { return names?.of(c) || c; } catch { return c; } };
  return ISO_COUNTRIES.map((code) => ({ code, name: name(code) })).sort((a, b) => a.name.localeCompare(b.name));
})();
// Wait this long before offering "Resend" after a successful send; the server's own limit
// (HTTP 429 retryInSec) overrides it whenever it says otherwise.
const RESEND_COOLDOWN_SEC = 30;

const clean = (v) => (v == null ? "" : String(v));
const pick = (src, details) => Object.fromEntries(FIELDS.map((k) => [k, clean(src?.[k] ?? details?.[DETAIL_KEY[k]])]));
const same = (a, b) => clean(a).trim() === clean(b).trim();
const mmss = (ms) => { const s = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

function validate(f, required) {
  const e = {};
  const v = (k) => clean(f[k]).trim();
  if (required && !v("billing_company")) e.billing_company = "Enter your company or trading name.";
  if (required && !v("billing_address")) e.billing_address = "Enter your business address.";
  if (required && !v("billing_country")) e.billing_country = "Choose the country you are based in.";
  if (!v("billing_email")) { if (required) e.billing_email = "Enter a contact email address."; }
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v("billing_email"))) e.billing_email = "That doesn't look like an email address.";
  if (!v("billing_phone")) { if (required) e.billing_phone = "Enter a phone number we can reach you on."; }
  else if (!/^(\+|00)[0-9 ()./-]+$/.test(v("billing_phone")) || v("billing_phone").replace(/\D/g, "").length < 8 || v("billing_phone").replace(/\D/g, "").length > 17) {
    // International format only: the server refuses a number with no country code rather
    // than guess which country it belongs to.
    e.billing_phone = "Enter the number in international format, starting with + and the country code, e.g. +44 7700 900123.";
  }
  return e;
}

function Chip({ ok, children }) {
  return (
    <span className={`pill ${ok ? "pill-ok" : "pill-warn"}`} style={{ whiteSpace: "nowrap" }}>
      {ok ? <CheckCircle2 size={12} aria-hidden="true" /> : <AlertCircle size={12} aria-hidden="true" />}
      {children}
    </span>
  );
}

// Status chip + send / enter code / resend for one channel. Codes always go to the SAVED
// address, so nothing can be sent while the field holds an unsaved edit.
function VerifyChannel({ channel, state, obStatus, savedValue, dirty, canAct, onChanged, onRetryStatus }) {
  const isEmail = channel === "email";
  const noun = isEmail ? "email address" : "phone number";
  const [sent, setSent] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(null); // "send" | "confirm"
  const [error, setError] = useState(null);
  const [manual, setManual] = useState(null); // message once submitted for a manual check
  const [locked, setLocked] = useState(false); // too many attempts / expired → must resend
  const [justVerified, setJustVerified] = useState(false);
  const [expiresAt, setExpiresAt] = useState(0);
  const [retryAt, setRetryAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // A different saved address is a different thing to verify — drop any code in progress.
  useEffect(() => {
    setSent(false); setSentTo(""); setCode(""); setError(null); setManual(null);
    setLocked(false); setJustVerified(false); setExpiresAt(0); setRetryAt(0);
  }, [savedValue]);

  // The optimistic flag must not outlive the server saying otherwise (e.g. an operator revoke).
  const serverVerified = state?.verified;
  useEffect(() => { if (serverVerified === false) setJustVerified(false); }, [serverVerified]);

  // One 1s ticker, only while a countdown is actually running; cleared on unmount.
  const ticking = expiresAt > now || retryAt > now;
  useEffect(() => {
    if (!ticking) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ticking]);

  if (obStatus === "skipped") return null;
  const verified = !!state?.verified || justVerified;

  if (verified) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Chip ok>{isEmail && state?.viaSignIn ? "Verified through your sign-in" : "Verified"}</Chip>
      </div>
    );
  }
  if (obStatus === "loading") {
    return <div className="mt-2 text-xs" style={{ color: "var(--text-muted)" }}><Spinner className="mr-1 inline" /> Checking verification…</div>;
  }
  if (obStatus !== "ready") {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
        <span>We couldn't check whether this {noun} is verified.</span>
        <button type="button" className="btn btn-secondary" style={{ padding: "3px 9px", fontSize: 12 }} onClick={onRetryStatus}>Check again</button>
      </div>
    );
  }

  const send = async () => {
    setBusy("send"); setError(null);
    try {
      const r = await (isEmail ? api.sendEmailCode() : api.sendPhoneCode());
      if (!alive.current) return;
      if (r?.alreadyVerified) { setJustVerified(true); await onChanged(); return; }
      if (r?.method === "manual") { setManual(r.message || ""); setSent(false); await onChanged(); return; }
      const t = Date.now();
      setNow(t); setSent(true); setLocked(false); setCode("");
      setSentTo(r?.sentTo || savedValue);
      setExpiresAt(Number(r?.expiresInSec) > 0 ? t + Number(r.expiresInSec) * 1000 : 0);
      setRetryAt(t + RESEND_COOLDOWN_SEC * 1000);
    } catch (e) {
      if (!alive.current) return;
      if (e.status === 429) {
        const wait = Number(e.data?.retryInSec) > 0 ? Number(e.data.retryInSec) : 60;
        const t = Date.now();
        setNow(t); setRetryAt(t + wait * 1000);
        setError("We've sent a code very recently. Please wait a moment before asking for another.");
      } else setError(e.message || "Couldn't send the code. Try again.");
    } finally { if (alive.current) setBusy(null); }
  };

  const confirm = async () => {
    if (code.length !== 6 || busy) return;
    setBusy("confirm"); setError(null);
    try {
      await (isEmail ? api.confirmEmailCode(code) : api.confirmPhoneCode(code));
      if (!alive.current) return;
      setJustVerified(true); setSent(false); setCode(""); setExpiresAt(0); setRetryAt(0);
      await onChanged(); // re-read onboarding so the banner and checklist update too
    } catch (e) {
      if (!alive.current) return;
      if (e.code === "manual_review") { setManual(""); setSent(false); setCode(""); }
      else if (e.code === "invalid_code") setError("That code isn't right. Check it and try again.");
      else if (e.code === "expired_code") { setError("That code has expired. Send a new one."); setCode(""); setLocked(true); setExpiresAt(0); }
      else if (e.code === "too_many_attempts") { setError("Too many incorrect attempts. Send a new code and try again."); setCode(""); setLocked(true); }
      else setError(e.message || "Couldn't verify that code. Try again.");
    } finally { if (alive.current) setBusy(null); }
  };

  const waiting = retryAt > now;
  const expired = sent && expiresAt > 0 && expiresAt <= now;
  const manualMode = state?.method === "manual";
  // Survives a reload: the server remembers that this number was put forward for a manual check.
  const submitted = manual != null || !!state?.reviewRequested;
  let hint = null;
  if (!savedValue) hint = `Save ${isEmail ? "a contact email" : "a phone number"} above, then verify it.`;
  else if (dirty) hint = `Save your changes to verify the new ${noun}.`;
  else if (!canAct) hint = "Only an org owner can verify this.";

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-2">
        <Chip ok={false}>Not verified</Chip>
        {!submitted && !hint && (
          <button type="button" className="btn btn-secondary" style={{ padding: "4px 11px" }} disabled={!!busy || waiting} onClick={send}>
            {busy === "send" ? "Sending…"
              : waiting ? `${sent ? "Resend" : "Try again"} in ${mmss(retryAt - now)}`
              : sent ? "Resend code"
              : manualMode ? "Submit for verification" : "Send code"}
          </button>
        )}
      </div>

      {hint && <p className="mt-1.5 text-xs" style={{ color: "var(--text-muted)" }}>{hint}</p>}

      {submitted && !hint && (
        <p className="mt-1.5 text-xs" role="status" style={{ color: "var(--text)" }}>
          Submitted — we'll verify your number shortly.
          {manual && <span style={{ display: "block", color: "var(--text-muted)", marginTop: 2 }}>{manual}</span>}
        </p>
      )}

      {sent && !submitted && !hint && (
        <div className="mt-2">
          <p className="text-xs" style={{ color: "var(--text-muted)", overflowWrap: "anywhere" }}>
            We sent a 6-digit code to <strong style={{ color: "var(--text)" }}>{sentTo}</strong>.{" "}
            {expired ? <span style={{ color: "var(--err-text)" }}>That code has expired — send a new one.</span>
              : expiresAt > now ? `It expires in ${mmss(expiresAt - now)}.` : null}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <input
              className="input mono" style={{ width: 132, letterSpacing: "0.18em" }}
              inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000"
              aria-label={`6-digit code sent to your ${noun}`}
              value={code} disabled={expired || locked || busy === "confirm"}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } }}
            />
            <button type="button" className="btn btn-primary" disabled={code.length !== 6 || !!busy || expired || locked} onClick={confirm}>
              {busy === "confirm" ? "Verifying…" : "Verify"}
            </button>
          </div>
        </div>
      )}

      {error && <p role="alert" className="mt-1.5 text-xs" style={{ color: "var(--err-text)" }}>{error}</p>}
    </div>
  );
}

export default function CompanyDetailsCard() {
  const { user } = useAuth();
  const isOwner = user?.orgRole === "owner";
  const ob = useOnboarding();
  const location = useLocation();
  const cardRef = useRef(null);
  const [form, setForm] = useState(null);
  const [savedForm, setSavedForm] = useState(null);
  const [touched, setTouched] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let off = false;
    const seed = (src) => { if (off) return; const f = pick(src, null); setForm(f); setSavedForm(f); };
    api.orgBillingInfo().then(seed).catch(() => seed({}));
    return () => { off = true; };
  }, []);

  const loaded = !!form;
  const loadedRef = useRef(false);
  loadedRef.current = loaded;

  // A server that hasn't stored phone/country on billing-info yet still reports them on the
  // onboarding object — fill blanks from there once, without clobbering anything typed.
  const details = ob.data?.details;
  const filled = useRef(false);
  useEffect(() => {
    if (!details || !loadedRef.current || filled.current) return;
    filled.current = true;
    const fill = (f) => (f ? Object.fromEntries(FIELDS.map((k) => [k, f[k] || clean(details[DETAIL_KEY[k]])])) : f);
    setSavedForm((f) => fill(f));
    setForm((f) => fill(f));
  }, [details, loaded]);

  // Deep link from the banner / checklist: /settings#company-details
  useEffect(() => {
    if (loaded && location.hash === "#company-details") cardRef.current?.scrollIntoView({ block: "start" });
  }, [loaded, location.hash, location.key]);

  // Required for customers. An operator's own account is exempt from the deploy gate, so
  // nothing is forced on them — the fields are just their invoice details.
  const required = ob.applies;
  const errors = form ? validate(form, required) : {};
  const shown = (k) => (showAll || touched[k]) && errors[k];
  const dirty = !!form && !!savedForm && FIELDS.some((k) => !same(form[k], savedForm[k]));

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setSavedFlag(false); setErr(null); };
  const blur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const save = async (e) => {
    e?.preventDefault();
    if (!form || busy) return;
    if (Object.keys(errors).length) { setShowAll(true); return; }
    setBusy(true); setErr(null); setSavedFlag(false);
    try {
      const body = Object.fromEntries(FIELDS.map((k) => [k, clean(form[k]).trim()]));
      const res = await api.saveOrgBillingInfo(body);
      const next = pick({ ...body, ...(res && typeof res === "object" ? res : {}) }, null);
      setForm(next); setSavedForm(next); setSavedFlag(true); setShowAll(false); setTouched({});
      // Changing the email or phone un-verifies that channel on the server — re-read it so
      // the chips, the banner and the create forms all reflect that immediately.
      await ob.refresh();
    } catch (e2) { setErr(e2); } finally { setBusy(false); }
  };

  const Req = () => (required ? <span aria-hidden="true" style={{ color: "var(--err-text)", marginLeft: 3 }}>*</span> : null);
  const field = (k, label, props = {}, isRequired = true) => (
    <div style={{ minWidth: 0 }}>
      <label className="label" htmlFor={`cd-${k}`}>{label}{isRequired ? <Req /> : <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}> (optional)</span>}</label>
      <input
        id={`cd-${k}`} className="input" value={form[k]} disabled={!isOwner}
        required={required && isRequired} aria-invalid={!!shown(k)} aria-describedby={shown(k) ? `cd-${k}-err` : undefined}
        style={shown(k) ? { borderColor: "var(--err)" } : undefined}
        onChange={set(k)} onBlur={blur(k)} {...props}
      />
      {shown(k) && <p id={`cd-${k}-err`} role="alert" className="mt-1 text-xs" style={{ color: "var(--err-text)" }}>{errors[k]}</p>}
    </div>
  );

  return (
    <div ref={cardRef} id="company-details" style={{ scrollMarginTop: 12 }}>
      <Card className="mb-4">
        <form onSubmit={save} noValidate>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div style={{ minWidth: 0 }}>
              <div className="flex items-center gap-2" style={{ color: "var(--text)" }}>
                <Building2 size={16} /><span className="text-sm font-semibold">Company details</span>
                {ob.status === "ready" && ob.applies && !ob.data?.exempt && (
                  ob.data?.complete
                    ? <span className="pill pill-ok">Complete</span>
                    : <span className="pill pill-warn">Incomplete</span>
                )}
              </div>
              <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
                Used on your invoices{required ? ", and required — with a verified email address and phone number — before any service can be deployed" : ""}.
                {!isOwner && " Only an org owner can edit these."}
              </p>
            </div>
            {isOwner && (
              <button type="submit" className="btn btn-primary" disabled={busy || !form || (!dirty && !showAll && savedFlag)}>
                {busy ? "Saving…" : savedFlag && !dirty ? "Saved ✓" : "Save details"}
              </button>
            )}
          </div>

          {!form && <div className="text-xs" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2 inline" /> Loading…</div>}
          {form && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {field("billing_company", "Company name", { placeholder: "Company Ltd", autoComplete: "organization" })}
                <div style={{ minWidth: 0 }}>
                  <label className="label" htmlFor="cd-billing_country">Country<Req /></label>
                  <select
                    id="cd-billing_country" className="select" value={form.billing_country} disabled={!isOwner}
                    required={required} aria-invalid={!!shown("billing_country")} autoComplete="country"
                    style={shown("billing_country") ? { borderColor: "var(--err)" } : undefined}
                    onChange={set("billing_country")} onBlur={blur("billing_country")}
                  >
                    <option value="">Select a country…</option>
                    {/* A value saved before this was a list (or by an older server) stays selectable. */}
                    {form.billing_country && !ISO_COUNTRIES.includes(form.billing_country) && <option value={form.billing_country}>{form.billing_country}</option>}
                    {COUNTRY_OPTIONS.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                  {shown("billing_country") && <p role="alert" className="mt-1 text-xs" style={{ color: "var(--err-text)" }}>{errors.billing_country}</p>}
                </div>
                <div className="sm:col-span-2" style={{ minWidth: 0 }}>
                  <label className="label" htmlFor="cd-billing_address">Address<Req /></label>
                  <textarea
                    id="cd-billing_address" className="input" value={form.billing_address} disabled={!isOwner}
                    required={required} aria-invalid={!!shown("billing_address")} placeholder="Street, town / city, postcode" autoComplete="street-address"
                    style={{ width: "100%", minHeight: 64, resize: "vertical", ...(shown("billing_address") ? { borderColor: "var(--err)" } : {}) }}
                    onChange={set("billing_address")} onBlur={blur("billing_address")}
                  />
                  {shown("billing_address") && <p role="alert" className="mt-1 text-xs" style={{ color: "var(--err-text)" }}>{errors.billing_address}</p>}
                </div>
                <div style={{ minWidth: 0 }}>
                  {field("billing_email", "Contact email", { type: "email", placeholder: "accounts@company.com", autoComplete: "email" })}
                  <VerifyChannel
                    channel="email" state={ob.data?.email} obStatus={ob.status}
                    savedValue={clean(savedForm?.billing_email).trim()} dirty={!same(form.billing_email, savedForm?.billing_email)}
                    canAct={isOwner} onChanged={ob.refresh} onRetryStatus={ob.refresh}
                  />
                </div>
                <div style={{ minWidth: 0 }}>
                  {field("billing_phone", "Phone number", { type: "tel", placeholder: "+44 7700 900123", autoComplete: "tel" })}
                  <VerifyChannel
                    channel="phone" state={ob.data?.phone} obStatus={ob.status}
                    savedValue={clean(savedForm?.billing_phone).trim()} dirty={!same(form.billing_phone, savedForm?.billing_phone)}
                    canAct={isOwner} onChanged={ob.refresh} onRetryStatus={ob.refresh}
                  />
                </div>
                {field("billing_vat", "VAT number", { placeholder: "GB 000 0000 00" }, false)}
              </div>
              {required && <p className="mt-3 text-xs" style={{ color: "var(--text-muted)" }}><span style={{ color: "var(--err-text)" }}>*</span> Required before you can deploy.</p>}
              {showAll && Object.keys(errors).length > 0 && (
                <p role="alert" className="mt-2 text-xs" style={{ color: "var(--err-text)" }}>Fix the highlighted fields, then save.</p>
              )}
              {err && <p role="alert" className="mt-2 text-xs" style={{ color: "var(--err-text)" }}>{err.message}</p>}
            </>
          )}
        </form>
      </Card>
    </div>
  );
}
